import React from 'react';
import { useAppSelector } from '../../../store/hooks';
import { Order } from '../../../types/order';
import { formatCurrency } from '../../../utils/currencyUtils';

interface PricingBreakdownProps {
  order: Order;
}

export const PricingBreakdown: React.FC<PricingBreakdownProps> = ({ order }) => {
  // Get recipes, deals, zones, and ranges from Redux
  const fillings = useAppSelector(state => state.recipes.fillings);
  const components = useAppSelector(state => state.recipes.components);
  const deals = useAppSelector(state => state.pricing.deals);
  const deliveryZones = useAppSelector(state => state.pricing.deliveryZones);
  const deliveryRanges = useAppSelector(state => state.pricing.deliveryRanges);

  // Only show pricing breakdown for finalized orders
  if (!order.isFinalized || !order.pricing) {
    return null;
  }

  const { pricing } = order;

  // Combine fillings and components for recipe lookup
  const allRecipes = [...fillings, ...components];

  // Find deal by ID
  const findDeal = (dealId: string | undefined) => {
    if (!dealId) return null;
    return deals.find(d => d.id === dealId);
  };

  // Find zone by ID
  const findZone = (zoneId: string | undefined) => {
    if (!zoneId) return null;
    return deliveryZones.find(z => z.id === zoneId);
  };

  // Find range by ID
  const findRange = (rangeId: string | undefined) => {
    if (!rangeId) return null;
    return deliveryRanges.find(r => r.id === rangeId);
  };

  // Get recipe name by ID
  const getRecipeName = (recipeId: string): string => {
    const recipe = allRecipes.find(r => r.id === recipeId);
    return recipe?.name || 'Unknown Recipe';
  };

  return (
    <div
      style={{
        backgroundColor: '#f5f5f5',
        border: '1px solid #ddd',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '20px'
      }}
    >
      <h3
        style={{
          margin: '0 0 16px 0',
          fontSize: '1.1rem',
          fontWeight: 'bold',
          color: '#333',
          borderBottom: '2px solid #2196F3',
          paddingBottom: '8px'
        }}
      >
        Pricing Breakdown
      </h3>

      {/* Item Lines */}
      <div style={{ marginBottom: '16px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #ddd' }}>
              <th style={{ textAlign: 'left', padding: '8px', fontWeight: 'bold' }}>Recipe</th>
              <th style={{ textAlign: 'center', padding: '8px', fontWeight: 'bold' }}>Quantity</th>
              <th style={{ textAlign: 'right', padding: '8px', fontWeight: 'bold' }}>Price/Dozen</th>
              <th style={{ textAlign: 'right', padding: '8px', fontWeight: 'bold' }}>Line Total</th>
            </tr>
          </thead>
          <tbody>
            {pricing.itemPrices.map((itemPrice, index) => {
              const lineTotal = itemPrice.quantity * itemPrice.pricePerDozen;
              const deal = findDeal(itemPrice.dealId);
              const recipeName = getRecipeName(itemPrice.recipeId);

              return (
                <tr key={index} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '8px' }}>
                    <div>{recipeName}</div>
                    {deal && (
                      <div
                        style={{
                          fontSize: '0.85rem',
                          color: '#4CAF50',
                          fontStyle: 'italic',
                          marginTop: '4px'
                        }}
                      >
                        Deal applied: {deal.quantity} dozen @ {formatCurrency(deal.price)}/dozen
                      </div>
                    )}
                  </td>
                  <td style={{ textAlign: 'center', padding: '8px' }}>{itemPrice.quantity} dozen</td>
                  <td style={{ textAlign: 'right', padding: '8px' }}>{formatCurrency(itemPrice.pricePerDozen)}</td>
                  <td style={{ textAlign: 'right', padding: '8px', fontWeight: 'bold' }}>
                    {formatCurrency(lineTotal)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Totals Section */}
      <div
        style={{
          borderTop: '2px solid #ddd',
          paddingTop: '12px',
          marginTop: '12px'
        }}
      >
        {/* Subtotal */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '8px 0',
            fontSize: '1rem'
          }}
        >
          <span style={{ fontWeight: 'bold' }}>Subtotal:</span>
          <span>{formatCurrency(pricing.subtotal)}</span>
        </div>

        {/* Delivery Fee */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '8px 0',
            fontSize: '1rem'
          }}
        >
          <div>
            <div style={{ fontWeight: 'bold' }}>
              Delivery Fee:
              {order.deliveryType === 'Pickup' && (
                <span style={{ fontWeight: 'normal', color: '#666', marginLeft: '8px' }}>(Pickup)</span>
              )}
            </div>
            {pricing.deliveryZoneId &&
              (() => {
                const zone = findZone(pricing.deliveryZoneId);
                return zone ? (
                  <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '4px' }}>
                    Zone: {zone.towns.join(', ')}
                  </div>
                ) : null;
              })()}
            {pricing.deliveryRangeId &&
              (() => {
                const range = findRange(pricing.deliveryRangeId);
                return range ? (
                  <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '4px' }}>
                    Distance: {range.minDistance}km - {range.maxDistance}km
                  </div>
                ) : null;
              })()}
          </div>
          <span>{formatCurrency(pricing.deliveryFee)}</span>
        </div>

        {/* Total */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '12px 0',
            fontSize: '1.2rem',
            fontWeight: 'bold',
            borderTop: '2px solid #333',
            marginTop: '8px'
          }}
        >
          <span>Total:</span>
          <span style={{ color: '#2196F3' }}>{formatCurrency(pricing.total)}</span>
        </div>

        {/* Calculated timestamp */}
        <div
          style={{
            fontSize: '0.75rem',
            color: '#999',
            textAlign: 'right',
            marginTop: '8px'
          }}
        >
          Calculated: {new Date(pricing.calculatedAt).toLocaleString()}
        </div>
      </div>
    </div>
  );
};
